import { ArrowUpRight, ChevronRight, CircleCheckBig, Clock3, PlayCircle, UserRound, Users } from "lucide-react";
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";

import { CollectionSearch } from "../components/CollectionSearch";
import { CollectionView } from "../components/CollectionView";
import { EmptyState } from "../components/EmptyState";
import { ViewModeToggle } from "../components/ViewModeToggle";
import { matchesCollectionSearch } from "../lib/collection-search";
import { interventionColumns, interventionTitleLabel, toInterventionRecord } from "../lib/operational-collection-records";
import type { RouterOutputs, UserSession } from "../lib/trpc";
import { trpc } from "../lib/trpc";
import { useViewMode } from "../lib/view-mode";

type Intervention = RouterOutputs["interventions"]["list"][number];

const UNASSIGNED_GROUP = "Senza collaboratore";

function sameDay(value: Date | string, today: Date) {
  const date = new Date(value);
  return date.getFullYear() === today.getFullYear() && date.getMonth() === today.getMonth() && date.getDate() === today.getDate();
}

/**
 * Il quaderno di carta del cliente è organizzato per persona: sotto il nome, i lavori della giornata.
 * Un intervento con due assegnatari compare sotto entrambi, perché entrambi devono andarci.
 */
export function groupInterventionsByAssignee(items: Intervention[]) {
  const groups = new Map<string, { name: string; items: Intervention[] }>();
  for (const item of items) {
    const assignees = item.assignees.length > 0
      ? item.assignees
      : [{ userId: UNASSIGNED_GROUP, name: UNASSIGNED_GROUP }];
    for (const assignee of assignees) {
      const group = groups.get(assignee.userId) ?? { name: assignee.name, items: [] };
      group.items.push(item);
      groups.set(assignee.userId, group);
    }
  }
  return [...groups]
    .map(([userId, group]) => ({ userId, ...group }))
    .sort((left, right) => {
      if (left.name === UNASSIGNED_GROUP) return 1;
      if (right.name === UNASSIGNED_GROUP) return -1;
      return left.name.localeCompare(right.name, "it");
    });
}

const STATO_A_PAROLE: Record<Intervention["status"], string> = {
  in_progress: "In corso",
  scheduled: "Da fare",
  completed: "Fatto",
  cancelled: "Annullato",
};
const ORDINE_STATO: Record<Intervention["status"], number> = { in_progress: 0, scheduled: 1, completed: 2, cancelled: 3 };

/** Prima il lavoro in corso, poi quelli da fare in ordine di orario, in fondo quelli fatti. */
export function orderWorkerDay(items: Intervention[]) {
  return [...items].sort((left, right) =>
    ORDINE_STATO[left.status] - ORDINE_STATO[right.status]
    || new Date(left.scheduledStart).getTime() - new Date(right.scheduledStart).getTime());
}

/**
 * La giornata di chi lavora sul campo: niente contatori, filtri o viste da scegliere.
 * Il saluto, il lavoro in corso se c'e, e sotto i lavori da toccare per aprirli.
 */
function WorkerDay({ session, items, isLoading, isError, onRetry, activeTimer }: {
  session: UserSession;
  items: Intervention[];
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  activeTimer: { interventionId: string | null; startedAt: Date | string } | null | undefined;
}) {
  const ordinati = orderWorkerDay(items);
  const daFare = items.filter((item) => item.status === "scheduled" || item.status === "in_progress").length;
  return (
    <main className="page page--today">
      <header className="page-heading today-heading today-heading--worker">
        <div>
          <p className="eyebrow">{new Date().toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long" })}</p>
          <h1>Ciao {session.user.name.split(" ")[0]}.</h1>
          {!isLoading && !isError && <p>{daFare === 0 ? "Per oggi non hai altri lavori." : daFare === 1 ? "Oggi hai 1 lavoro da fare." : `Oggi hai ${daFare} lavori da fare.`}</p>}
        </div>
      </header>

      {/* Una voce libera del registro ore non ha un lavoro da aprire: il cartello resta per i lavori. */}
      {activeTimer?.interventionId && (
        <Link className="active-timer-banner" to={`/interventi/${activeTimer.interventionId}`}>
          <span className="active-timer-banner__pulse" />
          <span><strong>Stai lavorando</strong><small>Dalle {new Date(activeTimer.startedAt).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })}. Tocca per aprire il lavoro.</small></span>
          <ArrowUpRight />
        </Link>
      )}

      <section className="section-block" aria-label="I tuoi lavori di oggi">
        {isLoading && <p className="loading-copy">Carico i lavori di oggi...</p>}
        {!isLoading && isError && <EmptyState title="I lavori non si sono caricati"><button type="button" className="button button--outline" onClick={onRetry}>Riprova</button></EmptyState>}
        {!isLoading && !isError && ordinati.length === 0 && <EmptyState title="Nessun lavoro per oggi">Quando il titolare ti assegna un lavoro, lo trovi qui.</EmptyState>}
        {!isLoading && !isError && ordinati.length > 0 && (
          <ul className="my-day">
            {ordinati.map((item) => (
              <li key={item.id}>
                <Link className={`my-day__card my-day__card--${item.status}`} to={`/interventi/${item.id}`} aria-label={`Apri il lavoro ${item.siteName}, ${item.title}`}>
                  <span className="my-day__site">{item.siteName}</span>
                  <span className="my-day__state">{STATO_A_PAROLE[item.status]}</span>
                  <span className="my-day__work">{item.title}</span>
                  <span className="my-day__when">Ore {new Date(item.scheduledStart).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })}{item.siteAddress ? ` · ${item.siteAddress}` : ""}</span>
                  <ChevronRight className="my-day__go" size={28} aria-hidden="true" />
                </Link>
              </li>
            ))}
          </ul>
        )}
        <p><Link className="text-button" to="/interventi">Vedi anche i lavori degli altri giorni</Link></p>
      </section>
    </main>
  );
}

export function TodayPage({ session }: { session: UserSession }) {
  const interventions = trpc.interventions.list.useQuery();
  const activeTimer = trpc.interventions.activeTimer.useQuery();
  const [viewMode, setViewMode] = useViewMode("oggi");
  const [groupByAssignee, setGroupByAssignee] = useState(false);
  const [search, setSearch] = useState("");
  const today = new Date();
  const todayKey = today.toDateString();
  /** Sul foglio il giorno va scritto per esteso: "Oggi" da solo non dice niente domani. */
  const oggiLeggibile = today.toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
  const todayItems = useMemo(
    () => (interventions.data ?? []).filter((item) => sameDay(item.scheduledStart, new Date(todayKey))),
    [interventions.data, todayKey],
  );
  const filteredItems = useMemo(
    () => todayItems.filter((item, index) => {
      const record = toInterventionRecord(item, index);
      return matchesCollectionSearch(search, [record.title, ...Object.values(record.cells).map((cell) => cell?.text)]);
    }),
    [todayItems, search],
  );
  const records = useMemo(() => filteredItems.map(toInterventionRecord), [filteredItems]);
  const assigneeGroups = useMemo(() => groupInterventionsByAssignee(filteredItems), [filteredItems]);
  const completed = todayItems.filter((item) => item.status === "completed").length;
  const inProgress = todayItems.filter((item) => item.status === "in_progress").length;
  const hasItems = todayItems.length > 0;

  if (session.membership.role === "collaborator") {
    return (
      <WorkerDay
        session={session}
        items={todayItems}
        isLoading={interventions.isLoading}
        isError={!interventions.isLoading && interventions.isError}
        onRetry={() => void interventions.refetch()}
        activeTimer={activeTimer.data}
      />
    );
  }

  return (
    <main className="page page--today">
      <header className="page-heading today-heading">
        <div>
          <p className="eyebrow">{today.toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long" })}</p>
          <h1>Buon lavoro, {session.user.name.split(" ")[0]}.</h1>
          <p>La situazione operativa della tua squadra, in un colpo d’occhio.</p>
        </div>
        <Link className="button button--outline" to="/interventi">Vedi tutti <ArrowUpRight size={18} /></Link>
      </header>

      <section className="pulse-strip" aria-label="Riepilogo di oggi">
        <div><span className="pulse-strip__icon"><Clock3 /></span><p><strong>{todayItems.length}</strong><span>Interventi oggi</span></p></div>
        <div><span className="pulse-strip__icon"><PlayCircle /></span><p><strong>{inProgress}</strong><span>In corso</span></p></div>
        <div><span className="pulse-strip__icon"><CircleCheckBig /></span><p><strong>{completed}</strong><span>Completati</span></p></div>
      </section>

      {activeTimer.data && (
        <Link className="active-timer-banner" to={`/interventi/${activeTimer.data.interventionId}`}>
          <span className="active-timer-banner__pulse" />
          <span><strong>Timer attivo</strong><small>Hai un lavoro in corso dalle {new Date(activeTimer.data.startedAt).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })}</small></span>
          <ArrowUpRight />
        </Link>
      )}

      <section className="section-block">
        <div className="section-heading">
          <div><p className="eyebrow">Programma</p><h2>Gli interventi di oggi</h2></div>
          <div className="section-heading__actions">
            <CollectionSearch label="Cerca interventi di oggi" value={search} onChange={setSearch} placeholder="Titolo, struttura o collaboratore" />
            <div className="grouping-toggle" role="group" aria-label="Come raggruppare gli interventi">
              <button type="button" className={groupByAssignee ? "" : "active"} aria-pressed={!groupByAssignee} onClick={() => setGroupByAssignee(false)}>
                <Users size={17} aria-hidden="true" /> Tutti
              </button>
              <button type="button" className={groupByAssignee ? "active" : ""} aria-pressed={groupByAssignee} onClick={() => setGroupByAssignee(true)}>
                <UserRound size={17} aria-hidden="true" /> Per collaboratore
              </button>
            </div>
            <ViewModeToggle value={viewMode} onChange={setViewMode} />
          </div>
        </div>
        <div className="collection-section" data-view={viewMode}>
          {interventions.isLoading && <p className="loading-copy">Caricamento interventi...</p>}
          {!interventions.isLoading && interventions.isError && <EmptyState title="Impossibile caricare gli interventi"><button type="button" className="button button--outline" onClick={() => void interventions.refetch()}>Riprova</button></EmptyState>}
          {!interventions.isLoading && !interventions.isError && !hasItems && <EmptyState title="Nessun intervento oggi">Il programma è libero. Controlla i prossimi incarichi nella sezione Interventi.</EmptyState>}
          {!interventions.isLoading && !interventions.isError && hasItems && records.length === 0 && <EmptyState title="Nessun risultato">Prova a modificare la ricerca.</EmptyState>}
          {!interventions.isLoading && !interventions.isError && records.length > 0 && !groupByAssignee && (
            <CollectionView
              mode={viewMode}
              ariaLabel="Interventi di oggi"
              titleLabel={interventionTitleLabel}
              columns={interventionColumns}
              records={records}
              print={{ section: "Oggi", details: [oggiLeggibile, search ? `Ricerca: ${search}` : null] }}
            />
          )}
          {!interventions.isLoading && !interventions.isError && records.length > 0 && groupByAssignee && (
            <div className="assignee-groups">
              {assigneeGroups.map((group, indice) => (
                <section className="assignee-group" key={group.userId}>
                  <h3 className="assignee-group__name">
                    <UserRound size={18} aria-hidden="true" />
                    {group.name}
                    <span>{group.items.length} {group.items.length === 1 ? "intervento" : "interventi"}</span>
                  </h3>
                  <CollectionView
                    mode={viewMode}
                    ariaLabel={`Interventi di oggi di ${group.name}`}
                    titleLabel={interventionTitleLabel}
                    columns={interventionColumns}
                    records={group.items.map(toInterventionRecord)}
                    // Qui gli elenchi sono tanti quanti i collaboratori, ma il foglio e uno solo:
                    // pulsante e intestazione stanno sul primo, il nome di ognuno e gia nel titolo.
                    print={indice === 0
                      ? { section: "Oggi", details: [oggiLeggibile, "Per collaboratore", search ? `Ricerca: ${search}` : null] }
                      : undefined}
                  />
                </section>
              ))}
            </div>
          )}
        </div>
      </section>
    </main>
  );
}
